import { useCallback, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { useAccount } from 'wagmi';
import { TESTNET_FORM_URL } from '../constants';
import { localStorageKeys } from '../lib/localstorageKeys';
import { showToast } from '../lib/showToast';
import { walletUnexpectedlyNotConnectedToast } from '../lib/toasts';
import { useDepositsEnabled } from './useDepositsEnabled';
import { useModalState } from './useModalState';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';
import { useSubaccountBalance } from './useSubaccountBalance';
import { useWalletStorage } from './useWalletStorage';

export interface OnboardStep {
  id: string;
  title: string;
  completed: boolean;
  buttonLabel?: string;
  panelContent: React.ReactNode;
  prerequisiteMessage?: string;
  activate: ((fromBtnClick?: boolean) => void) | undefined;
}

export const generateDefaultSteps = (stepDetails: {
  isWalletConnected: boolean;
  termsAgreedAt: number | undefined;
  amountDeposited: bigint;
  onViewTermsClick: () => void;
  onDepositFundsClick: (fromBtnClick?: boolean) => void;
  onEnableTradingClick: () => void;
  isLinked: boolean;
}) => {
  return [
    {
      id: 'connect',
      title: 'Connect',
      completed: stepDetails.isWalletConnected,
      activate: () => {},
      panelContent: 'Connect your wallet to get started with trading.',
      prerequisiteMessage: 'Connect wallet first',
    },
    {
      id: 'terms',
      title: 'Terms & Privacy',
      // If signer is linked, user must have already agreed.
      completed: stepDetails.termsAgreedAt !== undefined || stepDetails.isLinked,
      activate: stepDetails.onViewTermsClick,
      buttonLabel: 'Deposit', // We show Deposit, but should open terms if not agreed.
      panelContent: 'Review and agree to the Terms of Use and Privacy Policy.',
      prerequisiteMessage: 'Agree to terms first',
    },
    {
      id: 'deposit',
      title: 'Deposit funds',
      completed: stepDetails.amountDeposited > 0n,
      activate: stepDetails.onDepositFundsClick,
      buttonLabel: stepDetails.amountDeposited > 0n ? 'Deposit more' : 'Deposit',
      panelContent: 'Deposit funds into your account to start trading.',
      prerequisiteMessage: 'Deposit first',
    },
    {
      id: 'enable-trading',
      title: 'Enable trading',
      completed: stepDetails.isLinked,
      activate: stepDetails.onEnableTradingClick,
      buttonLabel: stepDetails.isLinked ? 'Start Trading' : 'Enable Trading',
      panelContent: 'Enable trading to unlock all features.',
    },
  ] as const satisfies OnboardStep[];
};

export type OnboardStepId = ReturnType<typeof generateDefaultSteps>[number]['id'];

export const useOnboardSteps = () => {
  const depositsEnabled = useDepositsEnabled();
  const { getSubaccountData } = useWalletStorage();
  const setTermsModal = useModalState((state) => state.setTermsModal);
  const setDepositModal = useModalState((state) => state.setDepositModal);
  const setSigningMethodModal = useModalState((state) => state.setSigningModal);
  const { address, status } = useAccount();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: subaccountBalances, isLoading: loadingBalance } = useSubaccountBalance(
    selectedSubaccount?.id
  );
  const [termsAgreedAtByAccount, setTermsAgreedAtByAccount] = useLocalStorage<
    Record<string, number>
  >(localStorageKeys.termsAgreedAtByAccount, {});
  const termsAgreedAt = address ? termsAgreedAtByAccount[address] : undefined;

  const amountDeposited = subaccountBalances?.reduce((acc, { amount }) => acc + amount, 0n) || 0n;
  const subaccountData = getSubaccountData(address, selectedSubaccount?.id);

  const isLinked =
    !!subaccountData && (subaccountData.signingMethod === 'manual' || subaccountData.linked);

  const agreeToTerms = useCallback(() => {
    if (!address) {
      showToast(walletUnexpectedlyNotConnectedToast());
      setSigningMethodModal('closed');
      return;
    }

    setTermsAgreedAtByAccount((currentMap) => ({ ...currentMap, [address]: Date.now() }));
  }, [address, setSigningMethodModal, setTermsAgreedAtByAccount]);

  // TODO: Pending whitelist implementation in backend.
  const isGeoblocked = false;
  const isNotWhitelisted = false;

  const steps = useMemo(
    () =>
      generateDefaultSteps({
        isWalletConnected: status === 'connected' && Boolean(address),
        termsAgreedAt,
        amountDeposited: amountDeposited,
        onViewTermsClick: () => {
          setTermsModal('open');
        },
        onDepositFundsClick: (fromBtnClick) => {
          if (!depositsEnabled) {
            if (fromBtnClick) {
              location.href = TESTNET_FORM_URL;
            }
            return;
          }
          return setDepositModal({ openOrClosed: 'open', context: 'Onboard' });
        },
        onEnableTradingClick: () => {
          if (isLinked) {
            setSigningMethodModal('closed');
          } else {
            setSigningMethodModal('open');
          }
        },
        isLinked,
      }),
    [
      status,
      address,
      termsAgreedAt,
      amountDeposited,
      isLinked,
      setTermsModal,
      depositsEnabled,
      setDepositModal,
      setSigningMethodModal,
    ]
  );

  return {
    complete: isLinked,
    steps,
    isLoading: loadingBalance,
    termsAgreedAt,
    isGeoblocked,
    isNotWhitelisted,
    agreeToTerms,
  };
};
